import { Analytics } from '@cobliteam/analytics';
import useChatWidget from '@cobliteam/hooks/src/useChatWidget';
import { computePositionRect, POSITION_TRANSITION, useChatWidgetPositionStore } from '@cobliteam/hooks/src/useChatWidget/store';
import { usePermissions } from '@cobliteam/permissions';
import Button from '@cobliteam/ui/components/Button';
import Popover, { PopoverContent, PopoverTrigger } from '@cobliteam/ui/components/Popover';
import Icon from '@cobliteam/ui/icons';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useAIAssistant, useFloatingChat, useSchedulingAgent } from '../../store';
import AgentChatPanel from '../AgentChatPanel';
import AIIcon from '../AIIcon/AIIcon';

const FloatingChatButton = () => {
  const { openBlipChat, hide, isFooterVisible } = useChatWidget();
  const position = useChatWidgetPositionStore((s) => s.position);
  const { isAISidepanelOpen } = useAIAssistant();
  const { isAccessAllowed } = usePermissions();
  const canUseSchedulingAgent = isAccessAllowed('create-update-delete:crm');
  const schedulingThreadIdRef = useRef(crypto.randomUUID());
  const { blipChatOpen, setBlipChatOpen } = useFloatingChat();
  const [popoverOpen, setPopoverOpen] = useState(false);

  const { isOpen: agentChatOpen, initialMessage, open: openSchedulingAgent, close: closeSchedulingAgent } = useSchedulingAgent();

  const positionStyle = useMemo(() => {
    const { right, bottom } = computePositionRect(position, isFooterVisible);
    return { right: `${right}px`, bottom: `${bottom}px`, transition: POSITION_TRANSITION };
  }, [position, isFooterVisible]);

  useEffect(() => {
    hide(true);
    return () => hide(false);
  }, [hide]);

  useEffect(() => {
    document.body.classList.toggle('blip-chat-open', blipChatOpen);
    return () => document.body.classList.remove('blip-chat-open');
  }, [blipChatOpen]);

  const isAnyChatOpen = agentChatOpen || blipChatOpen;

  const handleCloseSchedulingAgent = (trigger: 'fab-toggle' | 'panel-close-button') => {
    if (!agentChatOpen) return;
    Analytics.track('Scheduling Agent closed', { component: 'floating-chat-button', trigger });
    closeSchedulingAgent();
  };

  const closeAllChats = () => {
    if (blipChatOpen) {
      openBlipChat(); // toggle closes it
      setBlipChatOpen(false);
    }
    handleCloseSchedulingAgent('fab-toggle');
  };

  const handleButtonClick = () => {
    if (isAnyChatOpen) {
      closeAllChats();
    }
  };

  const handleBlipChat = () => {
    openBlipChat();
    setBlipChatOpen(true);
    setPopoverOpen(false);
  };

  const handleAgentChat = () => {
    Analytics.track('Scheduling Agent opened', { component: 'floating-chat-button' });
    openSchedulingAgent();
    setPopoverOpen(false);
  };

  // Mirror AISidepanelStateSync's behavior on the legacy Blip widget: when the
  // AI sidepanel takes over the right side, the floating chat (and the agent
  // panel inside it) hide so they don't overlap. State is preserved via zustand
  // — when the sidepanel closes, this remounts and any open chat is restored.
  if (isAISidepanelOpen) return null;

  return (
    <>
      <div data-testid='floating-chat-button' className='fixed z-[1001]' style={positionStyle}>
        <Popover open={isAnyChatOpen ? false : popoverOpen} onOpenChange={isAnyChatOpen ? undefined : setPopoverOpen}>
          <PopoverTrigger asChild>
            <Button
              variant='contained'
              color='primary'
              icon={<Icon name={isAnyChatOpen ? 'x' : 'bot'} size='md' />}
              className='mr-2 h-14 w-14 rounded-full shadow-lg'
              onClick={handleButtonClick}
              aria-label={isAnyChatOpen ? 'Fechar chat' : 'Abrir chat'}
            />
          </PopoverTrigger>
          <PopoverContent align='end' className='mb-2 w-56 p-1'>
            <button
              data-testid='blip-chat-option'
              type='button'
              className='flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm hover:bg-gray-2'
              onClick={handleBlipChat}
            >
              <Icon name='headset' size='sm' className='text-gray-9' />
              <span>Atendimento Cobli</span>
            </button>
            {canUseSchedulingAgent && (
              <button
                data-testid='scheduling-agent-option'
                type='button'
                className='flex w-full items-center gap-1 rounded-lg py-2.5 pl-1 text-left text-sm hover:bg-gray-2'
                onClick={handleAgentChat}
              >
                <AIIcon iconSize='sm' variant='ghost' shape='rounded' className='m-0 size-4 p-0' />
                <span>Agendar instalação com IA</span>
              </button>
            )}
          </PopoverContent>
        </Popover>
      </div>

      {agentChatOpen && (
        <AgentChatPanel onClose={() => handleCloseSchedulingAgent('panel-close-button')} initialMessage={initialMessage} threadId={schedulingThreadIdRef.current} />
      )}
    </>
  );
};

export default FloatingChatButton;
